<script lang="ts">
  import type { Snippet } from "svelte"
  import type { PopoverMenuItem } from "@kontextwerk/web-sdk/ui/overlay/popover/types"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { mdiArchiveOutline, mdiCheck, mdiCloseCircleOutline, mdiFilePdfBox, mdiOpenInNew } from "@mdi/js"
  import type { WorkOrdersViewFrameContext } from "../../context"
  import {
    archiveWorkOrdersBatch,
    fetchWorkOrderCommunicationProofPdf,
    updateWorkOrder,
  } from "../../../../../../../../api/kehrblatt/work-orders"
  import { triggerObjectUrlDownload } from "../../../../../../_shared/_logic/object-url-download"
  import { getAdminApi } from "../../../../../../../../api/kehrblatt/client"
  import { frameConfig } from "@kontextwerk/control-web-frame/shared/logic/frame-state"
  import ActivityContextPanel from "@kontextwerk/control-web-frame/shared/modules/activity-context-panel"
  import { composeControlActivityCatalog } from "@kontextwerk/control-web-frame/shared/logic/activity"
  import { fetchWorkOrderLetter } from "../../../../../../../../api/kehrblatt/letters"
  import { spaNavigate } from "../../../../../../../../_shared/_logic/navigation"
  import { getApiError } from "../../../../../../../../api/error-handling"
  import { showApiErrorNotification } from "../../../../_shared/_logic/api-error-notifications"
  import {
    workOrderArchiveIssueMessage,
    workOrderArchiveProcessed,
  } from "../../../../../../_shared/_logic/batch-results"
  import { parseDateOnly } from "../../../../../../../_shared/_logic/timezones"
  import {
    buildArchiveWorkOrderApprovalDescription,
    WORK_ORDER_ACTION_TOOLTIP_CLASS,
    workOrderActionTooltips,
  } from "../../../../../../_shared/_logic/components-work-orders-action-tooltips"

  type Props = {
    viewFrameContext: WorkOrdersViewFrameContext
    children: Snippet<[PopoverMenuItem[]]>
  }

  const { viewFrameContext, children }: Props = $props()
  const activityCatalog = $derived(composeControlActivityCatalog($frameConfig?.activity?.catalog))

  let saving = $state(false)
  let confirmingArchive = $state(false)
  let openingAppointmentPortal = $state(false)
  let downloadingProof = $state(false)
  let failReason = $state<string>("NOT_REACHABLE")
  let failDescription = $state<string>("")
  let showFailModal = $state(false)
  let failTarget = $state<{ id: string; organizationId: string } | null>(null)

  const canManageScheduling = $derived(
    viewFrameContext.permissions.allows("kehrblatt.scheduling", "manage"),
  )
  const contextWorkOrder = $derived(viewFrameContext.workOrder ?? null)
  const currentWorkOrder = $derived(
    contextWorkOrder?.id === viewFrameContext.workOrderId &&
      contextWorkOrder?.organization_id === viewFrameContext.organizationId
      ? contextWorkOrder
      : null,
  )
  const canEdit = $derived(
    Boolean(
      canManageScheduling &&
      !viewFrameContext.subscriptionRestrictionActive &&
      currentWorkOrder?.can_manage,
    ),
  )
  const pastDue = $derived.by(() => {
    if (!currentWorkOrder?.due_date) return false

    const parsed = parseDateOnly(currentWorkOrder.due_date)

    if (!parsed) return false

    const today = new Date()
    today.setHours(23, 59, 59, 999)

    return parsed.getTime() < today.getTime()
  })
  const isArchived = $derived(Boolean(currentWorkOrder?.archived_at))
  const canArchive = $derived(Boolean(currentWorkOrder && canEdit && !isArchived))
  const canMarkFailed = $derived(
    Boolean(
      currentWorkOrder && canEdit && !isArchived && currentWorkOrder.status === "OPEN" && pastDue,
    ),
  )
  const failLimitMessage =
    "Limit erreicht: Es können höchstens 10 Aufträge pro Betrieb und Monat erfolglos abgeschlossen werden."

  const handleArchive = async () => {
    if (!currentWorkOrder || !viewFrameContext.organizationId || saving || confirmingArchive) return

    if (!canArchive) {
      newNotification({ class: "error", html: "Dieser Auftrag kann nicht archiviert werden." })

      return
    }

    const target = currentWorkOrder
    const organizationSegment = viewFrameContext.organizationSlug ?? viewFrameContext.organizationId
    let submitted = false
    confirmingArchive = true
    try {
      await newApproval({
        title: "Auftrag archivieren",
        description: buildArchiveWorkOrderApprovalDescription(1),
        approvalLabel: "Ja, archivieren",
        declineLabel: "Abbrechen",
        approvalCallback: async () => {
          if (
            submitted ||
            saving ||
            !canArchive ||
            currentWorkOrder?.id !== target.id ||
            currentWorkOrder?.organization_id !== target.organization_id
          )
            return

          submitted = true
          saving = true
          try {
            const response = await archiveWorkOrdersBatch([target.id])

            if (!workOrderArchiveProcessed(response, target.id)) {
              newNotification({
                class: "warning",
                html: workOrderArchiveIssueMessage(
                  response,
                  target.id,
                  "Auftrag wurde nicht archiviert.",
                ),
              })

              return
            }

            newNotification({ class: "success", html: "Auftrag archiviert." })

            if (
              currentWorkOrder?.id !== target.id ||
              currentWorkOrder?.organization_id !== target.organization_id
            )
              return

            spaNavigate(`/organizations/${encodeURIComponent(organizationSegment)}/work-orders`, {
              replace: true,
              noSearchParam: true,
            })
          } catch (err) {
            console.error("work_order_archive_failed", err)
            showApiErrorNotification(err, "Auftrag konnte nicht archiviert werden.", {
              includeRequestId: true,
            })
          } finally {
            saving = false
          }
        },
      })
    } finally {
      confirmingArchive = false
    }
  }

  const handleMarkFailed = async () => {
    if (!currentWorkOrder || !canMarkFailed || saving) return

    failTarget = { id: currentWorkOrder.id, organizationId: currentWorkOrder.organization_id }
    failReason = "NOT_REACHABLE"
    failDescription = ""
    showFailModal = true
  }

  const handleOpenAppointmentPortal = async () => {
    const targetWorkOrderId = currentWorkOrder?.id

    if (!targetWorkOrderId || isArchived || openingAppointmentPortal || saving) return

    openingAppointmentPortal = true
    const popup = window.open("about:blank", "_blank")

    if (popup) popup.opener = null

    try {
      const letter = await fetchWorkOrderLetter(targetWorkOrderId, { mode: "impersonal" })

      if (!letter.qr_url) {
        popup?.close()
        newNotification({ class: "error", html: "Terminseite konnte nicht geöffnet werden." })

        return
      }

      if (popup) {
        popup.location.href = letter.qr_url
      } else {
        window.open(letter.qr_url, "_blank", "noopener,noreferrer")
      }
    } catch (err) {
      console.error("work_order_appointment_portal_open_failed", err)
      popup?.close()
      showApiErrorNotification(err, "Terminseite konnte nicht geöffnet werden.")
    } finally {
      openingAppointmentPortal = false
    }
  }

  const handleDownloadCommunicationProof = async () => {
    const targetWorkOrderId = currentWorkOrder?.id

    if (!targetWorkOrderId || downloadingProof || saving) return

    downloadingProof = true
    try {
      const download = await fetchWorkOrderCommunicationProofPdf(targetWorkOrderId)
      const url = URL.createObjectURL(download.blob)
      triggerObjectUrlDownload({ objectUrl: url, filename: download.filename })
      URL.revokeObjectURL(url)
      newNotification({ class: "success", html: "Nachweis heruntergeladen." })
    } catch (err) {
      console.error("work_order_communication_proof_download_failed", err)
      showApiErrorNotification(err, "Nachweis konnte nicht heruntergeladen werden.")
    } finally {
      downloadingProof = false
    }
  }

  const submitMarkFailed = async () => {
    if (
      !currentWorkOrder ||
      !canMarkFailed ||
      currentWorkOrder.id !== failTarget?.id ||
      currentWorkOrder.organization_id !== failTarget?.organizationId ||
      saving
    )
      return

    const target = currentWorkOrder
    saving = true
    try {
      const updated = await updateWorkOrder(target.id, {
        status: "CANCELED",
        close_reason: failReason || null,
        close_description: failDescription?.trim?.() || null,
      })
      newNotification({ class: "success", html: "Auftrag erfolglos abgeschlossen." })
      showFailModal = false

      if (
        currentWorkOrder?.id !== target.id ||
        currentWorkOrder?.organization_id !== target.organization_id
      )
        return

      viewFrameContext.onContextChange({ workOrder: updated })
    } catch (err) {
      console.error("work_order_fail_failed", err)
      const normalized = getApiError(err, "Auftrag konnte nicht aktualisiert werden.")
      newNotification({
        class: "error",
        html: normalized.statusCode === 400 ? failLimitMessage : normalized.message,
      })
    } finally {
      saving = false
    }
  }

  const actions = $derived.by<PopoverMenuItem[]>(() => {
    const items: PopoverMenuItem[] = []

    if (canMarkFailed) {
      items.push({
        id: "mark-failed",
        label: "Erfolglos abschließen",
        icon: mdiCloseCircleOutline,
        disabled: saving,
        reason: workOrderActionTooltips.detailMarkFailed,
        tooltipClassName: WORK_ORDER_ACTION_TOOLTIP_CLASS,
        tone: "danger",
        onClick: handleMarkFailed,
      })
    }

    if (canArchive) {
      items.push({
        id: "archive",
        label: "Auftrag archivieren",
        icon: mdiArchiveOutline,
        disabled: saving || confirmingArchive,
        reason: workOrderActionTooltips.rowArchive,
        tooltipClassName: WORK_ORDER_ACTION_TOOLTIP_CLASS,
        tone: "danger",
        onClick: handleArchive,
      })
    }

    if (currentWorkOrder && !isArchived) {
      items.push({
        id: "appointment-portal",
        label: openingAppointmentPortal ? "Öffne…" : "Terminseite",
        icon: mdiOpenInNew,
        disabled: openingAppointmentPortal || saving,
        reason: workOrderActionTooltips.detailAppointmentPage,
        tooltipClassName: WORK_ORDER_ACTION_TOOLTIP_CLASS,
        separatorBefore: items.length > 0,
        onClick: handleOpenAppointmentPortal,
      })
    }

    if (currentWorkOrder) {
      items.push({
        id: "communication-proof",
        label: downloadingProof ? "Lade Nachweis…" : "Kommunikationsnachweis (PDF)",
        icon: mdiFilePdfBox,
        disabled: downloadingProof || saving,
        separatorBefore: items.length > 0 && !items[items.length - 1].separatorBefore,
        onClick: handleDownloadCommunicationProof,
      })
    }

    return items
  })
</script>

{#if currentWorkOrder && currentWorkOrder.id === viewFrameContext.workOrderId && currentWorkOrder.organization_id === viewFrameContext.organizationId}
  <ActivityContextPanel
    context={{
      organization_id: currentWorkOrder.organization_id,
      object_type: "work_order",
      object_id: currentWorkOrder.id,
    }}
    label={currentWorkOrder.order_number ? `Auftrag ${currentWorkOrder.order_number}` : "Auftrag"}
    operations={getAdminApi().activity}
    catalog={activityCatalog}
  />
{/if}
{@render children(actions)}

{#if showFailModal}
  <ModalDialog
    appearance="plain"
    title="Erfolglos abschließen"
    onClose={() => (showFailModal = false)}
  >
    <div class="fail-body form">
      <Input
        surfaceTone="light"
        brightBackground={true}
        type="select"
        placeholder="Grund"
        options={[
          { value: "NOT_REACHABLE", name: "Nicht erreichbar" },
          { value: "OTHER", name: "Sonstiges" },
        ]}
        bind:value={failReason}
        disabled={saving}
      />
      <Input
        surfaceTone="light"
        brightBackground={true}
        type="textarea"
        placeholder="Beschreibung (optional)"
        bind:value={failDescription}
        disabled={saving}
      />
    </div>

    {#snippet footerSnippet()}
      <div class="fail-actions">
        <CTA
          darkMode={false}
          type="primaryButton"
          content={saving ? "Speichere…" : "Erfolglos abschließen"}
          onClick={submitMarkFailed}
          icon={mdiCheck}
          disabled={saving}
        />
      </div>
    {/snippet}
  </ModalDialog>
{/if}

<style lang="less">
  .fail-body {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .fail-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
  }
</style>
